import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  BrowserRouter,
  HashRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PageViewTracker } from "@/components/PageViewTracker";
import { FloatingActions } from "@/components/FloatingActions";
import { AnalyticsConsentBanner } from "@/components/AnalyticsConsentBanner";
import { initThirdPartyAnalyticsIfConsented } from "@/lib/thirdPartyAnalytics";
import {
  currentRoutePath,
  QUERY_ERROR_MESSAGE,
  QUERY_ERROR_TOAST_ID,
  shouldToastQueryError,
} from "@/lib/queryErrors";
import Index from "./pages/Index";
import Admin from "./pages/Admin";
import AdminLogin from "./pages/AdminLogin";
import AdminResetPassword from "./pages/AdminResetPassword";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Produtos from "./pages/Produtos";
import ProdutoDetail from "./pages/ProdutoDetail";
import SignBudget from "./pages/SignBudget";
import SignContract from "./pages/SignContract";
import Feedback from "./pages/Feedback";
import Pay from "./pages/Pay";
import Contato from "./pages/Contato";
import Servicos from "./pages/Servicos";
import Sobre from "./pages/Sobre";
import PoliticaDePrivacidade from "./pages/PoliticaDePrivacidade";
import PrivacidadeAutomacaoInstagram from "./pages/PrivacidadeAutomacaoInstagram";
import TermosDeUso from "./pages/TermosDeUso";
import AutomacaoEmpresarial from "./pages/AutomacaoEmpresarial";
import AutomacaoCRM from "./pages/automacao/CRM";
import AutomacaoAtendimento from "./pages/automacao/Atendimento";
import AutomacaoErp from "./pages/automacao/Erp";
import Bio from "./pages/Bio";
import OAuthConsent from "./pages/OAuthConsent";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (_error, query) => {
      if (shouldToastQueryError(currentRoutePath(window.location), query.meta)) {
        toast.error(QUERY_ERROR_MESSAGE, { id: QUERY_ERROR_TOAST_ID });
      }
    },
  }),
});

initThirdPartyAnalyticsIfConsented();

// Suporte a HashRouter via variável de ambiente (fallback para ambientes sem rewrite)
const useHash = import.meta.env.VITE_USE_HASH === "true";
const Router = useHash ? HashRouter : BrowserRouter;

// Navegação SPA não rola sozinha: volta ao topo ao trocar de página e
// respeita âncora (`/servicos#consultoria`, links da home e do rodapé).
const PublicSiteLayout = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView();
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return (
    <div className="public-site min-h-screen">
      <Outlet />
    </div>
  );
};

interface AppProps {
  basename?: string;
}

const App = ({ basename = "/" }: AppProps) => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <Router basename={basename}>
        <AuthProvider>
          <PageViewTracker />
          <FloatingActions />
          <AnalyticsConsentBanner />
          <Routes>
            <Route element={<PublicSiteLayout />}>
              <Route path="/" element={<Index />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/projetos" element={<Projects />} />
              <Route path="/projetos/:id" element={<ProjectDetail />} />
              <Route path="/produtos" element={<Produtos />} />
              <Route path="/produtos/:slug" element={<ProdutoDetail />} />
              <Route path="/contato" element={<Contato />} />
              <Route path="/servicos" element={<Servicos />} />
              <Route path="/solucoes" element={<Navigate to="/servicos" replace />} />
              <Route path="/sobre" element={<Sobre />} />
              <Route path="/politica-de-privacidade" element={<PoliticaDePrivacidade />} />
              <Route
                path="/politica-privacidade-automacao-instagram"
                element={<PrivacidadeAutomacaoInstagram />}
              />
              <Route path="/termos-de-uso" element={<TermosDeUso />} />
              <Route path="/automacao-empresarial" element={<AutomacaoEmpresarial />} />
              <Route path="/automacao-empresarial/crm" element={<AutomacaoCRM />} />
              <Route path="/automacao-empresarial/atendimento" element={<AutomacaoAtendimento />} />
              <Route path="/automacao-empresarial/erp" element={<AutomacaoErp />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/sign/budget/:token" element={<SignBudget />} />
            <Route path="/sign/contract/:token" element={<SignContract />} />
            <Route path="/feedback/:token" element={<Feedback />} />
            <Route path="/pay/:token" element={<Pay />} />
            <Route path="/bio" element={<Bio />} />
            <Route path="/oauth/consent" element={<OAuthConsent />} />
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              }
            />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </Router>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
